import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Star, CreditCard, Shield, Zap } from "lucide-react";
import api from "@/config/api";
import { BRAND_LOGO_PATH } from "@/utils/constants";

export default function SubscribePage() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const loadPackages = useCallback(async () => {
    try {
      const data = await api.subscriptions.getPackages();
      setPackages(data);
    } catch (error) {
      console.error("Error loading packages:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkSubscriptionAndRedirect = useCallback(async () => {
    // Check if user is super admin - redirect to dashboard immediately
    const isSuperAdmin = user?.is_super_admin === true;

    if (isSuperAdmin) {
      navigate("/dashboard", { replace: true });
      return;
    }

    try {
      // Check if user has an active subscription
      const subscription = await api.subscriptions.getCurrent();
      if (
        subscription &&
        subscription.status === "active" &&
        subscription.payment_status === "paid"
      ) {
        // User has active subscription, redirect to dashboard
        navigate("/dashboard", { replace: true });
        return;
      }
    } catch (error) {
      // 404 or "No active subscription found" is expected - not an error
      // Only log if it's a different error
      if (
        !error.message?.includes("No active subscription found") &&
        !error.message?.includes("404") &&
        !error.message?.includes("Not found")
      ) {
        console.error("Error checking subscription:", error);
      }
    }
    // Only load packages if we're not redirecting
    setCheckingSubscription(false);
    loadPackages();
  }, [loadPackages, navigate, user?.is_super_admin]);

  useEffect(() => {
    if (isAuthenticated) {
      checkSubscriptionAndRedirect();
    } else {
      setCheckingSubscription(false);
      loadPackages();
    }
  }, [checkSubscriptionAndRedirect, isAuthenticated, loadPackages]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img
              src={BRAND_LOGO_PATH}
              alt="SafarCore"
              className="h-20 sm:h-24 w-auto object-contain min-h-[80px]"
            />
          </div>
          <div className="inline-flex items-center gap-2 bg-primary/15 px-4 py-2 rounded-full mb-4">
            <Shield className="w-5 h-5 text-secondary" />
            <span className="text-sm font-semibold text-secondary">
              {isAuthenticated
                ? "Choose Your Subscription Plan"
                : "Get Started Today"}
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {isAuthenticated ? "Subscribe to Continue" : "Select Your Plan"}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {isAuthenticated
              ? `Welcome back, ${user?.name}! Please select a subscription plan to access all features.`
              : "Choose the perfect plan for your travel business needs"}
          </p>
        </div>

        {loading || checkingSubscription ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {packages.map((pkg, index) => {
              let featuresList = [];
              if (pkg.features != null) {
                if (Array.isArray(pkg.features)) {
                  featuresList = pkg.features;
                } else if (typeof pkg.features === "string") {
                  try {
                    const parsed = JSON.parse(pkg.features);
                    featuresList = Array.isArray(parsed) ? parsed : [pkg.features];
                  } catch {
                    featuresList = [pkg.features];
                  }
                }
              }
              const isPopular = pkg.name
                ?.toLowerCase()
                .includes("professional");

              return (
                <div
                  key={pkg.id}
                  className={`group relative overflow-hidden bg-white rounded-3xl shadow-2xl hover:shadow-primary/20 transition-all duration-500 hover:scale-105 animate-slideUp ${
                    isPopular
                      ? "ring-4 ring-primary scale-105 md:scale-110"
                      : ""
                  }`}
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  {/* Background Pattern */}
                  <div
                    className="absolute inset-0 opacity-5 bg-cover bg-center"
                    style={{
                      backgroundImage:
                        "url('https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop')",
                    }}
                  ></div>

                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-accent via-primary to-secondary text-white text-center py-3 rounded-t-3xl shadow-lg">
                      <div className="flex items-center justify-center gap-2">
                        <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" />
                        <span className="font-bold text-lg">Most Popular</span>
                        <Star className="w-5 h-5 fill-yellow-300 text-yellow-300" />
                      </div>
                    </div>
                  )}

                  <div
                    className={`relative z-10 p-8 ${isPopular ? "pt-16" : ""}`}
                  >
                    <div className="text-center mb-6">
                      <h3 className="text-3xl font-bold text-gray-900 mb-3 group-hover:text-secondary transition-colors">
                        {pkg.name}
                      </h3>
                      <p className="text-gray-600 min-h-[3rem]">
                        {pkg.description}
                      </p>
                    </div>

                    <div className="text-center mb-8 p-6 bg-gradient-to-br from-sky-50 to-cyan-50 rounded-2xl border-2 border-primary/25">
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-4xl sm:text-5xl font-bold text-secondary">
                          Coming Soon
                        </span>
                        <span className="text-gray-600 text-lg">
                          Pricing update in progress
                        </span>
                      </div>
                    </div>

                    <ul className="space-y-4 mb-8">
                      {featuresList.map((feature, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-3 group/item"
                        >
                          <div className="mt-1 flex-shrink-0">
                            <div className="bg-gradient-to-br from-primary to-secondary p-1.5 rounded-lg group-hover/item:scale-110 transition-transform">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          </div>
                          <span className="text-gray-700 leading-relaxed">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <button
                      type="button"
                      disabled
                      className="w-full py-4 rounded-xl font-bold text-lg bg-gray-200 text-gray-500 cursor-not-allowed shadow-sm"
                    >
                      Coming Soon
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info Section */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-secondary" />
              <h3 className="text-2xl font-bold text-gray-900">
                Secure Payment Processing
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              All payments are processed securely through Stripe. Your
              subscription will be activated immediately after payment
              confirmation.
            </p>
            <div className="flex items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                <span>Instant Activation</span>
              </div>
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span>Secure Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <span>Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
